import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Switch,
  Typography,
} from '@mui/material';
import {
  ButtonSave,
  ButtonSaveTitleSmall,
  FormSettingBlock,
  FormSettingButtons,
  FormSettingStyledListItem,
} from './style';
import { FormattedMessage } from 'react-intl';

interface ReviewSettings {
  reviewsAutoPullSetting: boolean;
  reviewsAutoReplySetting: boolean;
  reviewAutoReplyEligibleRating: number[];
  reviewsAutoReplyEnglishTranslation: boolean;
  useReplyTemplate: boolean;
  replyTemplate: string;
}

interface ReviewSettingsProps {
  settings: ReviewSettings;
  updateSetting: <K extends keyof ReviewSettings>(key: K, value: ReviewSettings[K]) => void;
  setShowReviewTemplate: (value: React.SetStateAction<boolean>) => void;
  onSave: () => Promise<void>;
}

const ReviewAutomationSettings: React.FC<ReviewSettingsProps> = ({
  settings,
  updateSetting,
  setShowReviewTemplate,
  onSave,
}) => {
  const handleReviewRatingChange = (rating: number) => {
    if (settings.reviewAutoReplyEligibleRating.includes(rating)) {
      updateSetting(
        'reviewAutoReplyEligibleRating',
        settings.reviewAutoReplyEligibleRating.filter((r) => r !== rating),
      );
    } else {
      updateSetting('reviewAutoReplyEligibleRating', [
        ...settings.reviewAutoReplyEligibleRating,
        rating,
      ]);
    }
  };

  const handleSave = () => {
    if (
      settings.useReplyTemplate &&
      (!settings.replyTemplate || settings.replyTemplate.trim() === '')
    ) {
      alert('Please provide a reply template when "Enable Auto Reply Template" is enabled.');
      return;
    }
    onSave();
  };

  return (
    <>
      <FormSettingButtons>
        <FormSettingBlock>
          <Typography fontSize={'medium'}>
            <FormattedMessage
              id="storeDetails.reviewAutomationSettings.title.label"
              defaultMessage="Review Settings"
            />
          </Typography>
          <FormSettingStyledListItem>
            <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
              <FormGroup aria-label="review-autopull-setting" sx={{ alignItems: 'flex-start' }}>
                <FormControlLabel
                  label="Auto Pull"
                  control={
                    <Switch
                      size="small"
                      checked={settings.reviewsAutoPullSetting}
                      disabled={true}
                    />
                  }
                  sx={{ marginLeft: '0', justifyContent: 'space-between', width: '100%' }}
                  labelPlacement="end"
                />
              </FormGroup>
              <FormHelperText>
                <FormattedMessage
                  id="storeDetails.reviewAutomationSettings.autoPullStatus.helpertext"
                  defaultMessage="configure reviews to be automatically pulled everyday"
                />
              </FormHelperText>
            </FormControl>
          </FormSettingStyledListItem>
          <FormSettingStyledListItem>
            <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
              <FormGroup aria-label="review-autoreply-status" sx={{ alignItems: 'flex-start' }}>
                <FormControlLabel
                  label="AI Auto Reply"
                  control={
                    <Switch
                      size="small"
                      checked={settings.reviewsAutoReplySetting}
                      disabled={true}
                    />
                  }
                  sx={{ marginLeft: '0', justifyContent: 'space-between', width: '100%' }}
                  labelPlacement="end"
                />
              </FormGroup>
              <FormHelperText>
                <FormattedMessage
                  id="storeDetails.reviewAutomationSettings.autoreplyStatus.helpertext"
                  defaultMessage="configure reviews to be automatically replied by AI"
                />
              </FormHelperText>
            </FormControl>
          </FormSettingStyledListItem>
          <FormSettingStyledListItem>
            <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
              <FormGroup
                aria-label="review-autoreply-translation-status"
                sx={{ alignItems: 'flex-start' }}
              >
                <FormControlLabel
                  label="AI English Translation"
                  control={
                    <Switch
                      size="small"
                      checked={settings.reviewsAutoReplyEnglishTranslation}
                      onChange={(e) =>
                        updateSetting('reviewsAutoReplyEnglishTranslation', e.target.checked)
                      }
                      disabled={!settings.reviewsAutoReplySetting}
                    />
                  }
                  sx={{ marginLeft: '0', justifyContent: 'space-between', width: '100%' }}
                  labelPlacement="end"
                />
              </FormGroup>
              <FormHelperText>
                <FormattedMessage
                  id="storeDetails.reviewAutomationSettings.englishTranslation.helpertext"
                  defaultMessage="configure AI to use english translation in auto reply"
                />
              </FormHelperText>
            </FormControl>
          </FormSettingStyledListItem>
          <FormSettingStyledListItem>
            <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
              <FormGroup
                aria-label="review-autoreply-translation-status"
                sx={{ alignItems: 'flex-start' }}
              >
                <FormControlLabel
                  label="Enable Auto Reply Template"
                  control={
                    <Switch
                      size="small"
                      checked={settings.useReplyTemplate}
                      onChange={(e) => updateSetting('useReplyTemplate', e.target.checked)}
                      disabled={!settings.reviewsAutoReplySetting}
                    />
                  }
                  sx={{ marginLeft: '0', justifyContent: 'space-between', width: '100%' }}
                  labelPlacement="end"
                />
              </FormGroup>
              <FormHelperText>
                <FormattedMessage
                  id="storeDetails.reviewAutomationSettings.useReplyTemplate.helpertext"
                  defaultMessage="configure to use a reply template instead of AI-generated replies"
                />
              </FormHelperText>
              <ButtonSaveTitleSmall>
                <ButtonSave
                  onClick={() => setShowReviewTemplate(true)}
                  disabled={!settings.useReplyTemplate}
                >
                  {settings.replyTemplate !== '' ? 'Edit Template' : 'Set Template'}
                </ButtonSave>
              </ButtonSaveTitleSmall>
            </FormControl>
          </FormSettingStyledListItem>
          <FormSettingStyledListItem>
            <Typography fontSize={'small'}>
              <FormattedMessage
                id="storeDetails.reviewAutomationSettings.eligibleRating.label"
                defaultMessage="AI Auto Reply Eligible Ratings"
              />
            </Typography>
            <FormControl component="fieldset" variant="standard">
              <FormGroup aria-label="eligible-ratings" row>
                {Array.from({ length: 5 }, (_, i) => i + 1).map((rating) => (
                  <FormControlLabel
                    key={'rating-' + rating}
                    label={rating}
                    control={
                      <Checkbox
                        size="small"
                        checked={settings.reviewAutoReplyEligibleRating.includes(rating)}
                        onChange={() => handleReviewRatingChange(rating)}
                        disabled={!settings.reviewsAutoReplySetting}
                      />
                    }
                  />
                ))}
              </FormGroup>
              <FormHelperText>
                <FormattedMessage
                  id="storeDetails.reviewAutomationSettings.eligibleRating.helpertext"
                  defaultMessage="set rating(s) that should be replied automatically by AI"
                />
              </FormHelperText>
            </FormControl>
          </FormSettingStyledListItem>
        </FormSettingBlock>
        <FormSettingBlock>
          <ButtonSaveTitleSmall>
            <ButtonSave onClick={handleSave} disabled={!settings.reviewsAutoReplySetting}>
              <FormattedMessage
                id="storeDetails.reviewAutomationSettings.save.label"
                defaultMessage="Update Review Settings"
              />
            </ButtonSave>
          </ButtonSaveTitleSmall>
        </FormSettingBlock>
      </FormSettingButtons>
    </>
  );
};

export default ReviewAutomationSettings;
